import type { RecordState } from './StatusChip'
import { formatScheduleLabel, type TimeFormat } from '../../lib/timeFormat'

/**
 * Shared by the pay-period calendar and the copy-a-schedule screen, since
 * both are literally showing calendar days in the same card shape. See
 * docs/design/style-guide.md's calendar day spec. Split from DayCard.tsx so
 * that file only exports the component, which Fast Refresh requires.
 */
// The six status-code categories from docs/product/scheduling-model.md,
// plus `unscheduled`: not a status code at all, but the UI state for a
// calendar date that carries no entry because no pay period covers it yet.
// It gets a fill of its own for the same reason every real category does
// (docs/design/style-guide.md), rather than leaving those dates blank.
export type Category = 'work' | 'off' | 'leave' | 'training' | 'detail' | 'unavailable' | 'unscheduled'

export type DayEntry = {
  date: string
  weekday: string
  day: number
  month: string
  category: Category
  label: string
  detail?: string
  state?: RecordState
  // Whether the entry's status code counts as available in coverage, when the
  // entry came from one; see countsAsAvailable below for an entry without.
  countsAsAvailable?: boolean
}

// Whether an entry counts toward the available headcount coverage measures. The
// status code says so where the entry came from one; otherwise the category
// does, since work and training are available and the rest are not.
export function countsAsAvailable(entry: { category: Category; countsAsAvailable?: boolean }): boolean {
  return entry.countsAsAvailable ?? (entry.category === 'work' || entry.category === 'training')
}

// Background fills only: the border color is plain `border-line` wherever
// this renders, so it is not baked in here the way it used to be. The style
// guide's category fills: work in canvas, off in surface-2, leave in the
// warning tint, detail and temporary duty in the info tint, training in the
// primary tint, unavailable in the error tint, and unscheduled in the plain
// `surface` neutral. Every tint is a role at 10% opacity rather than a raw
// ramp step, so it carries that role's own dark-theme value instead of
// staying the light theme's fixed color and losing its text underneath.
// The status label text carries the meaning; the fill only reinforces it.
export const CATEGORY_FILL: Record<Category, string> = {
  work: 'bg-canvas',
  off: 'bg-surface-2',
  leave: 'bg-warning/10',
  training: 'bg-primary/10',
  detail: 'bg-info/10',
  unavailable: 'bg-error/10',
  unscheduled: 'bg-surface',
}

export function dayAriaLabel(entry: DayEntry, format: TimeFormat): string {
  const parts = [`${entry.weekday}, ${entry.month} ${entry.day}`, formatScheduleLabel(entry.label, format)]
  if (entry.detail) parts.push(entry.detail)
  return parts.join(', ')
}

// docs/product/scheduling-model.md's "Tour of duty": the hours an employee
// is scheduled to work in a pay period, default 80.
export const TOUR_OF_DUTY_HOURS = 80

// docs/product/scheduling-model.md's "Scheduled hours": "each entry's
// scheduled hours are its end minus its start minus the meal period." A
// work or training label is always "HHMM–HHMM" or "HHMM–HHMM · <detail>"
// in this pilot's data, the only place a start and end time exist to read;
// there is no meal-period field in that data yet, so none is deducted.
export function scheduledHours(entry: DayEntry): number {
  if (entry.category !== 'work' && entry.category !== 'training') return 0
  const match = entry.label.match(/^(\d{2})(\d{2})–(\d{2})(\d{2})/)
  if (!match) return 0
  const startMinutes = Number(match[1]) * 60 + Number(match[2])
  const endMinutes = Number(match[3]) * 60 + Number(match[4])
  // An end at or before the start is the next morning.
  return (endMinutes > startMinutes ? endMinutes - startMinutes : endMinutes + 24 * 60 - startMinutes) / 60
}

export function totalScheduledHours(days: DayEntry[]): number {
  return days.reduce((total, entry) => total + scheduledHours(entry), 0)
}

// "8.0" reads as an odd amount of precision for a shift that is exactly 8
// hours; this keeps a real fraction (a 7.5-hour shift, say) but drops a
// trailing .0 rather than always showing one decimal place.
export function formatHours(hours: number): string {
  return `${Number(hours.toFixed(1))} hrs`
}
